import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agentAudit,
  agentConnections,
  type GameDefDetail,
  type GameDefSummary,
  getGame,
  getRequest,
  getRun,
  labH2H,
  labMe,
  labSettingsGet,
  listGames,
  listRequests,
  listRuns,
} from '#shared/api/gamelab';
import { api, isApiError } from '@/lib/api';
import { offlineQuery, registerOfflinePrefetch } from '@/lib/query';

/**
 * Query keys and hooks for the Game Lab. Every key starts with 'gamelab', so one invalidate on the
 * prefix refreshes the lot. The shelf and the definitions of shelf games are marked offline, so a
 * phone on a court with no signal can still open a game and start a run.
 */

export const LAB_KEY = ['gamelab'] as const;

export const labKeys = {
  all: LAB_KEY,
  games: () => ['gamelab', 'games'] as const,
  game: (id: string) => ['gamelab', 'game', id] as const,
  requests: () => ['gamelab', 'requests'] as const,
  request: (id: string) => ['gamelab', 'request', id] as const,
  runs: (o: object = {}) => ['gamelab', 'runs', o] as const,
  run: (id: string) => ['gamelab', 'run', id] as const,
  me: (playerId?: string) => ['gamelab', 'me', playerId ?? 'me'] as const,
  h2h: (a: string, b: string) => ['gamelab', 'h2h', a, b] as const,
  settings: () => ['gamelab', 'settings'] as const,
  connections: () => ['gamelab', 'connections'] as const,
  audit: () => ['gamelab', 'audit'] as const,
};

export const isNotFound = (e: unknown): boolean => isApiError(e) && e.status === 404;

const fetchGames = async () => (await api.call(listGames, { query: {} })).items;
const fetchGame = (id: string) => api.call(getGame, { params: { id } });

/** Every game that is not discarded, newest change first. */
export function useGames() {
  return useQuery({ ...offlineQuery(labKeys.games(), fetchGames), staleTime: 20_000, refetchOnWindowFocus: true });
}

export function useGame(id: string | undefined) {
  return useQuery({
    ...offlineQuery(labKeys.game(id ?? ''), () => fetchGame(id as string)),
    enabled: !!id,
    staleTime: 15_000,
    retry: (n, e) => !isNotFound(e) && n < 2,
  });
}

export function useRequests() {
  return useQuery({
    queryKey: labKeys.requests(),
    queryFn: async () => (await api.call(listRequests, { query: {} })).items,
    staleTime: 15_000,
  });
}

export function useRequest(id: string) {
  return useQuery({
    queryKey: labKeys.request(id),
    queryFn: () => api.call(getRequest, { params: { id } }),
    staleTime: 10_000,
    retry: (n, e) => !isNotFound(e) && n < 2,
  });
}

export function useLiveRuns() {
  return useQuery({
    queryKey: labKeys.runs({ status: 'live' }),
    queryFn: async () => (await api.call(listRuns, { query: { status: 'live', limit: 10 } })).items,
    staleTime: 8_000,
    refetchInterval: 20_000,
    meta: { persist: false },
  });
}

export function useRecentRuns(sessionId?: string) {
  return useQuery({
    queryKey: labKeys.runs({ status: 'final', sessionId }),
    queryFn: async () => (await api.call(listRuns, { query: { status: 'final', sessionId, limit: 30 } })).items,
    staleTime: 20_000,
  });
}

export function useLabMe(playerId?: string) {
  return useQuery({
    queryKey: labKeys.me(playerId),
    queryFn: () => api.call(labMe, { query: playerId ? { playerId } : {} }),
    staleTime: 20_000,
  });
}

export function useLabH2H(a: string, b: string) {
  return useQuery({
    queryKey: labKeys.h2h(a, b),
    queryFn: () => api.call(labH2H, { query: { a, b } }),
    staleTime: 30_000,
    enabled: !!a && !!b,
  });
}

export function useLabSettings() {
  return useQuery({ queryKey: labKeys.settings(), queryFn: () => api.call(labSettingsGet, {}), staleTime: 30_000 });
}

export function useConnections(enabled = true) {
  return useQuery({
    queryKey: labKeys.connections(),
    queryFn: async () => (await api.call(agentConnections, {})).items,
    enabled,
    staleTime: 10_000,
    meta: { persist: false },
  });
}

export function useAudit(before?: number) {
  return useQuery({
    queryKey: [...labKeys.audit(), before ?? 'first'],
    queryFn: () => api.call(agentAudit, { query: { limit: 20, ...(before ? { before } : {}) } }),
    staleTime: 10_000,
    meta: { persist: false },
  });
}

export function useRun(id: string) {
  return useQuery({
    queryKey: labKeys.run(id),
    queryFn: () => fetchRun(id),
    staleTime: 5_000,
    retry: (n, e) => !isNotFound(e) && n < 1,
    refetchInterval: (q) => (q.state.data?.run.status === 'live' ? 5_000 : false),
    meta: { persist: false },
  });
}

export async function fetchRun(id: string) {
  return api.call(getRun, { params: { id } });
}

/** Games a member can play: the shelf, plus a ready game they are about to try. */
export const playable = (g: Pick<GameDefSummary, 'status'>): boolean => g.status === 'shelf' || g.status === 'ready';

/**
 * Register the offline prefetch: the shelf, and the definition of every playable game, so Play this
 * works with no signal. Runs after sign-in and on every `online` event.
 */
export function initLabOffline(): void {
  registerOfflinePrefetch('gamelab.shelf', async (qc) => {
    const games = await qc.fetchQuery({ ...offlineQuery(labKeys.games(), fetchGames), staleTime: 0 });
    await Promise.allSettled(
      games
        .filter(playable)
        .map((g) =>
          qc.prefetchQuery({ ...offlineQuery(labKeys.game(g.id), () => fetchGame(g.id)), staleTime: 60_000 }),
        ),
    );
  });
}

export function useInvalidateLab() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: LAB_KEY });
}

export type { GameDefDetail, GameDefSummary };
